import "dabbyjs/src/core/get/get.js";
import "dabbyjs/src/attributes/prop/prop.js";
import "dabbyjs/src/events/trigger/trigger.js";
import resize from "./image/resize.js";
import pdq from "@swgfl/pdq";
import md5 from "./md5/md5.js";
import pdna from "./photodna/photodna.js";

export default (obj, config, files) => {
	config = Object.assign({
		resize: 512,
		minsize: 128,
		debug: false,
		max: 20,
		minquality: 70
	}, config);

	// compile files
	const uploaded = [...obj.get(0).files];
	for (const file of uploaded) {

		// check we haven't got the file in the list already
		file.accept = files.every(item => ["lastModified", "name", "size", "type"].some(prop => item[prop] !== file[prop]));
	}

	return new Promise((resolve, reject) => {
		if (uploaded.length <= config.max) {
			let pos = 0,
				items = 0,
				start = performance.now();
			const update = () => {
				pos++;
				const percentage = (100 / items) * pos,
					time = performance.now(),
					elapsed = time - start;

				obj.trigger("hash.progress", {
					remain: (((elapsed / pos) * items) - elapsed) / 1000,
					percentage: percentage
				});
			},
				tasks = [];

			// setup tasks
			uploaded.filter(file => file.accept).forEach(file => {

				// hash images
				if (file.type.includes("image/")) {

					// map upload to image
					tasks.push(
						new Promise((resolve, reject) => {
							const url = URL.createObjectURL(file),
								img = new Image();
							img.onload = () => {
								resolve(img);
								URL.revokeObjectURL(url);
							};

							// a corrupt or unsupported image must not stall the whole batch
							img.onerror = () => {
								reject(new Error("The image could not be loaded"));
								URL.revokeObjectURL(url);
							};
							img.src = url;
						})

							// reject if too small, then count the hashes this file will generate
							.then(img => {
								if (img.width < config.minsize || img.height < config.minsize) {
									throw new Error("The image is too small");
								}
								items += 4;
								return img;
							})

							// resize image
							.then(img => {
								const min = Math.min(config.resize, img.width, img.height);
								return resize(img, min, min);
							})

							// generate hashes
							.then(img => {
								update(); // progress for resizing the image

								// generate PDQ hash
								return pdq(img, config)
									.then(hash => {
										update();

										// mark hashes that are too low quality
										if (hash.quality < config.minquality) {
											file.accept = false;

										// mark hashes that we have already generated (Duplicates)
										} else if (file.accept) {
											file.accept = files.concat(uploaded).every(file => file.pdq !== hash.hash);
										}
										file.pdq = hash.hash;
									})

									// generate PhotoDNA hash
									.then(() => {
										return pdna(img, config).then(pdnahash => {
											update();
											file.pdna = pdnahash.data[1]?.PhotoDna || pdnahash.data[0].PhotoDna; // use borderless image if available
										});
									});
							})
							.then(() => {
								return md5(file)
									.then(hash => {
										update();
										if (file.accept) {
											file.accept = files.concat(uploaded).every(file => file.md5 !== hash);
										}
										file.md5 = hash;
									});
							})

							// the image could not be hashed, so leave it out of the case
							.catch(() => {
								file.accept = false;
							})
					);

				// hash videos
				} else if (file.type.includes("video/")) {

					// always generate an MD5
					tasks.push(
						md5(file)
							.then(hash => {
								items++; // only counted once the file has been read
								update();
								if (file.accept) {
									file.accept = files.concat(uploaded).every(file => file.md5 !== hash);
								}
								file.md5 = hash;
							})

							// the video could not be read, so leave it out of the case
							.catch(() => {
								file.accept = false;
							})
					);
					// tasks.push(() => tmk(file));

				// wrong file type
				} else {
					file.accept = false;
				}
			});

			// run tasks
			Promise.all(tasks).then(() => resolve(uploaded));

		// too many files?
		} else {
			reject();
		}
	})
};